import React, { useState } from 'react';
import { Eye, Layers, Printer, Send, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { PageHeader } from '../../components/admin/PageHeader';
import { WorkflowStepper } from '../../components/admin/WorkflowStepper';
import { Panel } from '../../components/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { A4Frame } from '../../components/notice/A4Frame';
import { SecureNotice } from '../../components/notice/SecureNotice';
import { useDemo } from '../../contexts/DemoContext';
import { toBn } from '../../utils/bangla';
interface DispatchRow {
  caseNo: string;
  vehicle: string;
  noticeId: string;
  print: string;
  date: string;
  postalRef: string;
  status: string;
}
const initialRows: DispatchRow[] = [{
  caseNo: 'DMP/AI/2025/0614/0748',
  vehicle: 'ঢাকা মেট্রো-গ-২৩-৭১০৭',
  noticeId: 'DMP-DOC-2025-0748-V1',
  print: 'প্রিন্ট অপেক্ষমাণ',
  date: '—',
  postalRef: '—',
  status: 'অপেক্ষমাণ'
}, {
  caseNo: 'DMP/AI/2025/0613/0688',
  vehicle: 'ঢাকা মেট্রো-গ-৩৪-৯০১২',
  noticeId: 'DMP-DOC-2025-0688-V1',
  print: 'প্রিন্টকৃত',
  date: '১৪/০৬/২০২৫',
  postalRef: 'RL-DHK-552871034BD',
  status: 'প্রেরিত'
}, {
  caseNo: 'DMP/AI/2025/0612/0659',
  vehicle: 'ঢাকা মেট্রো-খ-১৯-৫৫৭০',
  noticeId: 'DMP-DOC-2025-0659-V1',
  print: 'প্রিন্টকৃত',
  date: '১৩/০৬/২০২৫',
  postalRef: 'RL-DHK-552870988BD',
  status: 'প্রেরিত'
}, {
  caseNo: 'DMP/AI/2025/0611/0617',
  vehicle: 'ঢাকা মেট্রো-ন-২৬-৩০৪১',
  noticeId: 'DMP-DOC-2025-0617-V1',
  print: 'প্রিন্টকৃত',
  date: '—',
  postalRef: '—',
  status: 'অপেক্ষমাণ'
}, {
  caseNo: 'DMP/AI/2025/0611/0623',
  vehicle: 'ঢাকা মেট্রো-গ-১৮-৭৭৪৫',
  noticeId: 'DMP-DOC-2025-0623-V1',
  print: 'প্রিন্ট অপেক্ষমাণ',
  date: '—',
  postalRef: '—',
  status: 'অপেক্ষমাণ'
}, {
  caseNo: 'DMP/AI/2025/0612/0644',
  vehicle: 'ঢাকা মেট্রো-গ-২১-৮৮০৩',
  noticeId: 'DMP-DOC-2025-0644-V2',
  print: 'প্রিন্টকৃত',
  date: '১২/০৬/২০২৫',
  postalRef: 'RL-DHK-552870412BD',
  status: 'ফেরত এসেছে'
}];
export function PrintDispatch() {
  const {
    completeStage,
    stage
  } = useDemo();
  const [rows, setRows] = useState(initialRows);
  const [selected, setSelected] = useState<string[]>([]);
  const [dispatchFor, setDispatchFor] = useState<DispatchRow | null>(null);
  const [ref, setRef] = useState('');
  const [refErr, setRefErr] = useState('');
  const [viewing, setViewing] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const update = (caseNo: string, patch: Partial<DispatchRow>) => setRows(rs => rs.map(r => r.caseNo === caseNo ? {
    ...r,
    ...patch
  } : r));
  const printOne = (r: DispatchRow) => {
    update(r.caseNo, {
      print: 'প্রিন্টকৃত'
    });
    toast.success(`${r.noticeId} প্রিন্টারে পাঠানো হয়েছে`);
  };
  const saveDispatch = () => {
    if (!dispatchFor) return;
    if (!/^RL-[A-Z]{3}-\d{9}BD$/.test(ref.trim())) return setRefErr('সঠিক ডাক রেফারেন্স দিন (যেমন RL-DHK-552871120BD)');
    update(dispatchFor.caseNo, {
      postalRef: ref.trim(),
      date: '১৫/০৬/২০২৫',
      status: 'প্রেরিত',
      print: 'প্রিন্টকৃত'
    });
    if (dispatchFor.caseNo.endsWith('0748')) completeStage(Math.max(stage, 7));
    toast.success('প্রেরণের তথ্য সংরক্ষিত হয়েছে', {
      description: `${dispatchFor.caseNo} • বাংলাদেশ ডাক বিভাগ`
    });
    setDispatchFor(null);
  };
  const toggle = (c: string) => setSelected(s => s.includes(c) ? s.filter(x => x !== c) : [...s, c]);
  const pending = rows.filter(r => r.print === 'প্রিন্ট অপেক্ষমাণ').length;
  return <div data-id="element-1406" data-mp="2pssmaktebaq4">
      <PageHeader screen="WEB SCREEN 12" title="নোটিশ প্রিন্ট ও ডাকযোগে প্রেরণ" subtitle="নিরাপদ নোটিশ প্রিন্ট, ব্যাচ প্রিন্টিং ও বাংলাদেশ ডাক বিভাগের রেফারেন্স সংরক্ষণ" actions={<Button disabled={!selected.length} onClick={() => setBatchOpen(true)} data-id="element-1408" data-mp="1ecvm20g3x4dl">
            <Layers className="h-4 w-4" data-id="element-1409" data-mp="19eam824rompo" /> ব্যাচ প্রিন্ট ({toBn(selected.length)})
          </Button>} data-id="element-1407" data-mp="2pskayxcx8dh1" />
      <WorkflowStepper current={6} data-id="element-1410" data-mp="2xags8r0wazyu" />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4" data-id="element-1411" data-mp="32308xwuvqb7v">
        {[['প্রিন্ট অপেক্ষমাণ', pending, '#E69A20'], ['প্রিন্টকৃত', rows.filter(r => r.print === 'প্রিন্টকৃত').length, '#1769AA'], ['প্রেরিত', rows.filter(r => r.status === 'প্রেরিত').length, '#16834A'], ['ফেরত এসেছে', rows.filter(r => r.status === 'ফেরত এসেছে').length, '#D93036']].map(([l, v, c]) => <div key={l as string} className="rounded-lg border border-border bg-white p-4" data-id="element-1412" data-mp="3l0un0pav7cx4">
            <div className="text-sm text-slate-600" data-id="element-1413" data-mp="371bgg0xpc9mx">{l}</div>
            <div className="mt-1 text-2xl font-bold" style={{
          color: c as string
        }} data-id="element-1414" data-mp="3c1fvrnmviybk">{toBn(v as number)}</div>
          </div>)}
      </div>

      <Panel bodyClassName="overflow-x-auto" data-id="element-1415" data-mp="1vmepglhyjacx">
        <table className="w-full min-w-[1100px] text-sm" data-id="element-1416" data-mp="20matgkqnn1sh">
          <thead className="bg-[#003B71] text-left text-xs text-white" data-id="element-1417" data-mp="20nvoebgvsmqv">
            <tr data-id="element-1418" data-mp="1s4ikcxwwb6so">
              <th className="px-3 py-3" data-id="element-1419" data-mp="1s3z2fh78wien"><input type="checkbox" aria-label="সব নির্বাচন" className="h-4 w-4" checked={selected.length === rows.length} onChange={e => setSelected(e.target.checked ? rows.map(r => r.caseNo) : [])} data-id="element-1420" data-mp="1s3h1465q7dzv" /></th>
              {['মামলা নম্বর', 'গাড়ির নম্বর', 'নোটিশ ID', 'প্রিন্ট অবস্থা', 'প্রেরণের তারিখ', 'ডাক রেফারেন্স', 'প্রেরণের অবস্থা', 'অ্যাকশন'].map(h => <th key={h} className="px-3 py-3 font-medium" data-id="element-1421" data-mp="19e0wb6n0czdn">{h}</th>)}
            </tr>
          </thead>
          <tbody data-id="element-1422" data-mp="14gh7jcsop47g">
            {rows.map(r => <tr key={r.caseNo} className="border-t border-border hover:bg-[#F4F7FB]" data-id="element-1423" data-mp="14dmp1qt8dtgo">
                <td className="px-3 py-3" data-id="element-1424" data-mp="1j5wxwroaz7mm"><input type="checkbox" aria-label="নির্বাচন" className="h-4 w-4" checked={selected.includes(r.caseNo)} onChange={() => toggle(r.caseNo)} data-id="element-1425" data-mp="1edvoddiirv0a" /></td>
                <td className="px-3 py-3 font-mono text-xs text-[#003B71]" data-id="element-1426" data-mp="qmq8wzghr4qi">{r.caseNo}</td>
                <td className="px-3 py-3" data-id="element-1427" data-mp="loeyonmaas7r">{r.vehicle}</td>
                <td className="px-3 py-3 font-mono text-xs" data-id="element-1428" data-mp="2lrpikcei1b6u">{r.noticeId}</td>
                <td className="px-3 py-3" data-id="element-1429" data-mp="2lpmvo18n7y6c"><StatusBadge label={r.print} data-id="element-1430" data-mp="2qqaj6cxnxwvt" /></td>
                <td className="px-3 py-3" data-id="element-1431" data-mp="2qoprqw6nau26">{r.date}</td>
                <td className="px-3 py-3 font-mono text-xs" data-id="element-1432" data-mp="2vgr22ck96apk">{r.postalRef}</td>
                <td className="px-3 py-3" data-id="element-1433" data-mp="2zium3pkzmnmg"><StatusBadge label={r.status} data-id="element-1434" data-mp="2zj43ioryvmgj" /></td>
                <td className="px-3 py-3" data-id="element-1435" data-mp="2zkyno0vezajk">
                  <div className="flex flex-wrap gap-1" data-id="element-1436" data-mp="22tdayv38k9dd">
                    <Button size="xs" variant="outline" onClick={() => printOne(r)} data-id="element-1437" data-mp="22uy4qihq7oa6"><Printer className="h-3.5 w-3.5" data-id="element-1438" data-mp="27shk5lq5u73p" /> প্রিন্ট করুন</Button>
                    <Button size="xs" variant="outline" disabled={r.status === 'প্রেরিত'} onClick={() => {
                  setDispatchFor(r);
                  setRef('');
                  setRefErr('');
                }} data-id="element-1439" data-mp="2bt0bzfs1zd5w"><Truck className="h-3.5 w-3.5" data-id="element-1440" data-mp="2gtxq9h9xzn24" /> প্রেরণের তথ্য যোগ করুন</Button>
                    <Button size="xs" variant="ghost" onClick={() => setViewing(true)} data-id="element-1441" data-mp="3n7ndd8qo07aq"><Eye className="h-3.5 w-3.5" data-id="element-1442" data-mp="3s709kk3feqpz" /> নোটিশ দেখুন</Button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Panel>

      <Dialog open={!!dispatchFor} onOpenChange={o => !o && setDispatchFor(null)} data-id="element-1443" data-mp="179bajzee21p">
        <DialogContent data-id="element-1444" data-mp="3slne8dkoduq2">
          <DialogHeader data-id="element-1445" data-mp="3sl5bcy3ici96">
            <DialogTitle data-id="element-1446" data-mp="3skc8473rcavs">প্রেরণের তথ্য যোগ করুন</DialogTitle>
            <DialogDescription data-id="element-1447" data-mp="3okbnu5tz95gt">{dispatchFor?.caseNo} • {dispatchFor?.noticeId}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 px-6 pb-4" data-id="element-1448" data-mp="3jm0ahl4h4ctu">
            <label className="block text-sm" data-id="element-1449" data-mp="3jim7qbrwe1lv">
              <span className="mb-1 block font-medium" data-id="element-1450" data-mp="3elm9uo57c55u">ডাক রেফারেন্স নম্বর</span>
              <Input value={ref} onChange={e => {
              setRef(e.target.value.toUpperCase());
              setRefErr('');
            }} placeholder="RL-DHK-552871120BD" className="font-mono" data-id="element-1451" data-mp="39m9etg46frs6" />
              {refErr && <span className="mt-1 block text-xs text-[#D93036]" data-id="element-1452" data-mp="35nk27pqyqe9l">{refErr}</span>}
              <button type="button" onClick={() => setRef('RL-DHK-552871120BD')} className="mt-1 text-xs text-[#1769AA] hover:underline" data-id="element-1453" data-mp="17tbt048thzgq">নমুনা রেফারেন্স বসান</button>
            </label>
            <label className="block text-sm" data-id="element-1454" data-mp="1n348ctvpkzrl">
              <span className="mb-1 block font-medium" data-id="element-1455" data-mp="1r3xvhl1buajf">প্রেরণের পদ্ধতি</span>
              <select className="h-9 w-full rounded-md border border-input px-3 text-sm" data-id="element-1456" data-mp="1r60li52hc3o5"><option data-id="element-1457" data-mp="1dc9ilfj97i1e">রেজিস্টার্ড ডাক (প্রাপ্তি স্বীকারপত্রসহ)</option><option data-id="element-1458" data-mp="1ibcdw89fm9w6">সাধারণ ডাক</option></select>
            </label>
            <label className="block text-sm" data-id="element-1459" data-mp="1i9j96yidpzqt">
              <span className="mb-1 block font-medium" data-id="element-1460" data-mp="13dk1hoz6m1m7">প্রেরণের তারিখ</span>
              <Input defaultValue="১৫/০৬/২০২৫" data-id="element-1461" data-mp="18e7qy67riaeb" />
            </label>
          </div>
          <DialogFooter data-id="element-1462" data-mp="udx0h4ad6xto">
            <Button variant="outline" onClick={() => setDispatchFor(null)} data-id="element-1463" data-mp="ueovacw50riw">বাতিল</Button>
            <Button onClick={saveDispatch} data-id="element-1464" data-mp="zchzx1hsldh2"><Send className="h-4 w-4" data-id="element-1465" data-mp="zdt2wz3eyh74" /> প্রেরিত হিসেবে চিহ্নিত করুন</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewing} onOpenChange={setViewing} data-id="element-1466" data-mp="2oq3nd0cmf31t">
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl" data-id="element-1467" data-mp="2tppzt7kmkf9k">
          <DialogHeader data-id="element-1468" data-mp="33u6ehxmdbqbs"><DialogTitle data-id="element-1469" data-mp="33w0zfbxn3ifv">প্রিন্টযোগ্য নোটিশ প্রিভিউ</DialogTitle></DialogHeader>
          <div className="bg-slate-100 p-4" data-id="element-1470" data-mp="2ywend5tjs88n"><A4Frame data-id="element-1471" data-mp="2yx65bdu827gg"><SecureNotice data-id="element-1472" data-mp="2yxg0dfu4j4t0" /></A4Frame></div>
          <DialogFooter data-id="element-1473" data-mp="3cn9qs2la1x4u"><Button onClick={() => window.print()} data-id="element-1474" data-mp="3cnt7jfz6yfha"><Printer className="h-4 w-4" data-id="element-1475" data-mp="3coujrnry180l" /> প্রিন্ট করুন</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen} data-id="element-1476" data-mp="37v9qg91ap27q">
        <DialogContent className="sm:max-w-3xl" data-id="element-1477" data-mp="3mkeck1b7nql4">
          <DialogHeader data-id="element-1478" data-mp="3mnaahpn26f9k">
            <DialogTitle data-id="element-1479" data-mp="3hmmip1r09uea">ব্যাচ প্রিন্ট — {toBn(selected.length)}টি নোটিশ</DialogTitle>
            <DialogDescription data-id="element-1480" data-mp="3vm5f4xagfhar">প্রিন্টার: HP LaserJet M507 (ট্রাফিক সদর দপ্তর) • A4 • একপাশে</DialogDescription>
          </DialogHeader>
          <div className="grid max-h-[55vh] grid-cols-2 gap-3 overflow-y-auto px-6 pb-4 md:grid-cols-4" data-id="element-1481" data-mp="3rllkq1h9mrsb">
            {rows.filter(r => selected.includes(r.caseNo)).map(r => <div key={r.caseNo} className="rounded-md border border-border bg-slate-50 p-2" data-id="element-1482" data-mp="1x711zuci96bk">
                <A4Frame data-id="element-1483" data-mp="27tgfg5qz88va"><SecureNotice data-id="element-1484" data-mp="27t6z79vqhg2s" /></A4Frame>
                <div className="mt-1 truncate font-mono text-[10px]" data-id="element-1485" data-mp="27u9rnm8pfqkt">{r.noticeId}</div>
              </div>)}
          </div>
          <DialogFooter data-id="element-1486" data-mp="1ys0gcu5m3t6d">
            <Button variant="outline" onClick={() => setBatchOpen(false)} data-id="element-1487" data-mp="1yrgzzhvm1cug">বাতিল</Button>
            <Button onClick={() => {
            setRows(rs => rs.map(r => selected.includes(r.caseNo) ? {
              ...r,
              print: 'প্রিন্টকৃত'
            } : r));
            toast.success(`${toBn(selected.length)}টি নোটিশ প্রিন্টারে পাঠানো হয়েছে`);
            setSelected([]);
            setBatchOpen(false);
          }} data-id="element-1488" data-mp="1ttp46cruh6kz">
              <Printer className="h-4 w-4" data-id="element-1489" data-mp="2mjxrej18mh6f" /> ব্যাচ প্রিন্ট করুন
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}